import { CalendarDefinition } from "../types/public-types";

/** Parse "HH:MM" or "HH:MM:SS" into minutes since midnight. */
export function parseTimeToMinutes(t: string): number {
  const parts = t.split(":").map(Number);
  return parts[0] * 60 + parts[1] + (parts[2] ? parts[2] / 60 : 0);
}

/** Format a "YYYY-MM-DD" string from a Date (local). */
function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** True if the date falls on a calendar holiday. */
export function isHoliday(date: Date, cal: CalendarDefinition): boolean {
  if (cal.holidays && cal.holidays.length > 0) {
    return cal.holidays.includes(toDateString(date));
  } else {
    return false;
  }
}

/** True if the date's weekday is in off_days or is a holiday. */
export function isOffDay(date: Date, cal: CalendarDefinition): boolean {
  if (cal.off_days && cal.off_days.length > 0) {
    return cal.off_days.includes(date.getDay()) || isHoliday(date, cal);
  } else {
    return false;
  }
}

/** True if the date is a regular working day (not off_day, not holiday). */
export function isWorkingDay(date: Date, cal: CalendarDefinition): boolean {
  return !isOffDay(date, cal);
}

/**
 * Returns the shift windows (in minutes since midnight) for a given date.
 * Returns [] for off days / holidays.
 */
export function getShiftsForDay(
  date: Date,
  cal: CalendarDefinition
): Array<{ start: number; end: number }> {
  if (isOffDay(date, cal) || !cal.shifts) return [];
  return cal.shifts.map(([s, e]) => ({
    start: parseTimeToMinutes(s),
    end: parseTimeToMinutes(e),
  }));
}

/**
 * Returns all working time intervals between start and end, respecting:
 * - off days and holidays (entire day skipped)
 * - shift windows (multiple shifts per day are supported)
 * - partial first/last days clipped to the actual start/end time
 */
export function getWorkingIntervals(
  start: Date,
  end: Date,
  cal: CalendarDefinition
): Array<{ start: Date; end: Date }> {
  if (start >= end) return [];
  const intervals: Array<{ start: Date; end: Date }> = [];

  // Iterate day by day
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);

  while (cursor <= end) {
    const shifts = getShiftsForDay(cursor, cal);

    for (const shift of shifts) {
      // Build shift start/end as Date objects for this day
      const shiftStart = new Date(cursor);
      shiftStart.setHours(
        Math.floor(shift.start / 60),
        Math.floor(shift.start % 60),
        Math.round((shift.start % 1) * 60),
        0
      );
      const shiftEnd = new Date(cursor);
      shiftEnd.setHours(
        Math.floor(shift.end / 60),
        Math.floor(shift.end % 60),
        Math.round((shift.end % 1) * 60),
        0
      );

      // Clip to [start, end]
      const intervalStart = shiftStart < start ? start : shiftStart;
      const intervalEnd = shiftEnd > end ? end : shiftEnd;

      if (intervalStart < intervalEnd) {
        intervals.push({ start: intervalStart, end: intervalEnd });
      }
    }

    // Advance to the next day
    cursor.setDate(cursor.getDate() + 1);
  }

  return intervals;
}

/**
 * Snaps a date to the nearest valid working moment.
 * direction "forward"  → move forward until inside a shift
 * direction "backward" → move backward until inside a shift
 * Searches up to 60 days to avoid infinite loops.
 */
export function snapToWorkingTime(
  date: Date,
  cal: CalendarDefinition,
  direction: "forward" | "backward"
): Date {
  const MAX_DAYS = 60;
  const result = new Date(date);

  for (let attempt = 0; attempt < MAX_DAYS * 24 * 60; attempt++) {
    const dayStart = new Date(result);
    dayStart.setHours(0, 0, 0, 0);
    const shifts = getShiftsForDay(dayStart, cal);

    if (shifts.length > 0) {
      if (direction === "forward") {
        // Find first shift window that contains or is after result
        const currentMins =
          result.getHours() * 60 +
          result.getMinutes() +
          result.getSeconds() / 60;
        for (const shift of shifts) {
          if (currentMins <= shift.end) {
            // Snap to start of shift if before it
            if (currentMins < shift.start) {
              result.setHours(
                Math.floor(shift.start / 60),
                Math.round(shift.start % 60),
                0,
                0
              );
            }
            return result;
          }
        }
        // All shifts passed — move to next day's first shift
        result.setDate(result.getDate() + 1);
        result.setHours(0, 0, 0, 0);
      } else {
        // direction === "backward"
        const currentMins =
          result.getHours() * 60 +
          result.getMinutes() +
          result.getSeconds() / 60;
        // Find last shift window that contains or is before result
        for (let i = shifts.length - 1; i >= 0; i--) {
          if (currentMins >= shifts[i].start) {
            if (currentMins > shifts[i].end) {
              result.setHours(
                Math.floor(shifts[i].end / 60),
                Math.round(shifts[i].end % 60),
                0,
                0
              );
            }
            return result;
          }
        }
        // All shifts ahead — move to previous day's last shift
        result.setDate(result.getDate() - 1);
        result.setHours(23, 59, 59, 0);
      }
    } else {
      // Off day — move to boundary
      if (direction === "forward") {
        result.setDate(result.getDate() + 1);
        result.setHours(0, 0, 0, 0);
      } else {
        result.setDate(result.getDate() - 1);
        result.setHours(23, 59, 59, 0);
      }
    }
  }

  // Fallback: return original if no valid time found
  return date;
}

/**
 * Returns the most recent week-start date on or before the given date,
 * where weekStartDay is a JS day index (0=Sun, 1=Mon, ..., 6=Sat).
 */
export function getWeekStartDate(date: Date, weekStartDay: number): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dayOfWeek = d.getDay(); // 0–6
  const diff = (dayOfWeek - weekStartDay + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

/**
 * Returns the quarter number (1–4) for a date, given a fiscal quarterStart month (0–11, 0=Jan).
 * e.g. quarterStart=4 means Q1 starts in May.
 */
export function getQuarterNumber(
  date: Date,
  quarterStart: number
): number {
  const month = date.getMonth(); // 0–11
  // Offset month relative to quarter start
  const offset = ((month - quarterStart + 12) % 12);
  return Math.floor(offset / 3) + 1;
}

/**
 * Returns the quarter start month (0-11) for a given date and fiscal quarterStart (0-indexed).
 * Useful for labeling (e.g. Q1 starting in May → returns 4).
 */
export function getQuarterStartMonth(
  date: Date,
  quarterStart: number
): number {
  const q = getQuarterNumber(date, quarterStart);
  // q=1 → month = quarterStart, q=2 → +3, q=3 → +6, q=4 → +9
  return (quarterStart + (q - 1) * 3) % 12; // 0-indexed
}
